import { getSignal, VehicleFuelType } from '@api/index';
import { getFeatureToggles, getPerformVehicles } from '@common/permissions/selectors';
import Box from '@components/Box';
import OperatingPeriod from '@components/OperatingPeriod';
import VehiclesInfoTabs from '@components/VehiclesInfoTabs';
import { Loadable } from '@data/loadable';
import OperationalDays from '@features/operationalDays';
import PDFDownloader from '@features/pdf/detail/DriverPDFDownloader';
import { driverSidebarData, hideSidebar, showSidebar, SidebarData } from '@features/ui/reducer';
import { getActiveDateRange } from '@features/ui/selectors';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { combustionColumnNames, commonColumnNames, electricColumnNames, getColumns } from '@utils/columns';
import { convertToRows } from '@utils/convertToRows';
import { extractDriverName } from '@utils/stringFormatters';
import { trackingAttributes } from '@utils/tracking';
import _ from 'lodash';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { Column as ColumnType } from '../../columns/createColumn';
import { DRIVER_ROUTE } from '../../constants/routes';
import { State } from '../../setup/types';
import { DateRange, Driver, HydratedEntity, HydratedEntityWithChildren, Vehicle } from '../../types';
import { getTopColumns } from './columns';
import { openRow } from './driverAnalysisReducer';
import Column from './DriverColumn';
import Tabs from './DriverSidebarTabs';
import useDriverContentLoader from './hooks/useDriverContentLoader';
import { Data } from './types';

const convertToRow = convertToRows(3);

export enum TabType {
    FUEL_TAB = 'fuelVehicles',
    ELECTRIC_TAB = 'electricVehicles',
}

export const FormattedOperationDays = ({ entity }: { entity: HydratedEntity }) => (
    <div className=" margin-bottom-5">
        <label className="margin-bottom-0 text-size-12">
            <FormattedMessage id="operationDays" />
        </label>
        <div className="text-size-18 ellipsis-1">
            <OperationalDays
                operatingDays={getSignal(entity, 'operatingDays', undefined)}
                totalNumberOfDays={getSignal(entity, 'totalNumberOfDays', undefined)}
            />
        </div>
    </div>
);

export type DriverSidebarData = SidebarData &
    Omit<HydratedEntityWithChildren, 'children' | 'drivers' | 'vehicles'> &
    Partial<Pick<HydratedEntityWithChildren, 'children' | 'drivers' | 'vehicles'>> & {
        driverIds: string[];
        electricVehicles: Vehicle[];
        fuelVehicles: Vehicle[];
        electricVehicleIds: string[];
        fuelVehicleIds: string[];
        containsElectricVehicle: boolean;
        containsCombustionVehicle: boolean;
        isVehiclesNameClickable: boolean;
    };

export const DriverSidebar: React.FC<{
    data?: DriverSidebarData;
    onClose: () => void;
    dateRange: DateRange;
    onVehicleClick: (vehicle: Data) => void;
    openParent: (id?: string) => void;
    allowedVehicles: string[];
    shouldShowOvertakeInCruise?: boolean;
    shouldUseV3Ratings: boolean;
}> = ({
    data,
    onClose,
    dateRange,
    onVehicleClick,
    openParent,
    allowedVehicles,
    shouldShowOvertakeInCruise = false,
    shouldUseV3Ratings = false,
}) => {
    if (!data) {
        return null;
    }

    const [tabKey, setTabKey] = useState<TabType>(TabType.FUEL_TAB);

    const currentData: { vehicleIds: string[]; vehicles: Vehicle[] } =
        tabKey === TabType.ELECTRIC_TAB
            ? { vehicleIds: data.electricVehicleIds, vehicles: data.electricVehicles }
            : { vehicleIds: data.fuelVehicleIds, vehicles: data.fuelVehicles };

    const loaderData = useDriverContentLoader({
        selectedDriverIds: data.driverIds ?? [],
        dateRange: dateRange,
        shouldShowHarshKPIs: false,
        shouldUseV2Ratings: false,
        shouldShowExcessiveKPIs: false,
        shouldUseV3Ratings,
        vehicles: currentData.vehicleIds,
    });

    const onVehiclesNameClick = useCallback(
        (vehicle: Data) => {
            onVehicleClick(vehicle);
            openParent(data.id);
        },
        [onVehicleClick, openParent, data]
    );

    let tabEntity = Loadable.cata(
        loaderData,
        data => data[0],
        () => [],
        () => [],
        () => undefined
    ) as DriverSidebarData | undefined;

    if (!tabEntity && data.vehicles?.length !== 1) {
        return null;
    }

    const childrenData = {
        electric:
            data.children?.filter(child =>
                child.vehicles?.every(vehicle => vehicle.fuelType === VehicleFuelType.ELECTRIC)
            ) ?? [],
        fuel:
            data.children?.filter(child =>
                child.vehicles?.every(vehicle => vehicle.fuelType !== VehicleFuelType.ELECTRIC)
            ) ?? [],
    };

    const currentChildren = tabKey === TabType.ELECTRIC_TAB ? childrenData.electric : childrenData.fuel;

    if (tabEntity) {
        tabEntity.drivers = data.drivers;
        tabEntity.vehicles = currentData.vehicles;
        tabEntity.children = currentChildren;
        tabEntity.childrenCount = currentChildren.length;
    }

    const isVehiclesNameClickable = data.isVehiclesNameClickable ?? false;
    const topColumns = getTopColumns(isVehiclesNameClickable, onVehiclesNameClick as (vehicle: unknown) => void);

    const topRows = convertToRow(topColumns.map(column => <Column key={column?.key} column={column} entity={data} />));

    const startDate = dateRange.start;
    const endDate =
        dateRange.end.getHours() === 0 && dateRange.end.getMinutes() === 0
            ? moment(dateRange.end)
                  .subtract(1, 'minutes')
                  .toDate()
            : dateRange.end;

    const commonColumns = getColumns(commonColumnNames);
    const combustionColumns = getColumns(combustionColumnNames).concat(commonColumns);
    const electricColumns = getColumns(electricColumnNames).concat(commonColumns);
    const containsElectricVehicle = data.containsElectricVehicle ?? false;
    const containsCombustionVehicle = data.containsCombustionVehicle ?? false;

    const displayedCombustionVehicleColumns = combustionColumns
        .map((column: ColumnType) => <Column key={column.key} column={column} entity={data} />)
        .concat([<FormattedOperationDays key="operationDays" entity={data as HydratedEntityWithChildren} />]);
    const displayedElectricVehicleColumns = electricColumns
        .map((column: ColumnType) => <Column key={column.key} column={column} entity={data} />)
        .concat([<FormattedOperationDays key="operationDays" entity={data as HydratedEntityWithChildren} />]);

    const driver = extractDriverName(_.get(data, 'drivers[0]', {}) as Driver);
    const vehicleCombustionRows = convertToRow(displayedCombustionVehicleColumns);
    const vehicleElectricRows = convertToRow(displayedElectricVehicleColumns);

    return (
        <Sidebar
            fly
            onClose={onClose}
            position={'right'}
            width={1000}
            title={
                <div
                    className="padding-left-10 justify-content-between flex align-items-center"
                    data-test="DriverSidebar"
                    {...trackingAttributes(
                        'visibility',
                        'perform, detailsPanel',
                        'openDriverDetailsPanel',
                        'openDetailsPanel, drivingAnalysis'
                    )}
                >
                    <FormattedMessage
                        id="driverAnalysis.sidebar.title.oneDriver"
                        values={{
                            driver,
                            start: <FormattedDate value={startDate} />,
                            end: <FormattedDate value={endDate} />,
                        }}
                    />
                </div>
            }
            headerButtons={
                <PDFDownloader
                    entity={data as HydratedEntityWithChildren}
                    dateRange={dateRange}
                    shouldUseV3Ratings={shouldUseV3Ratings}
                />
            }
            bodyClassName={'overflow-y-scroll'}
        >
            <OperatingPeriod
                start={getSignal<Date | string | undefined>(data, 'start', undefined) as Date | string}
                end={getSignal<Date | string | undefined>(data, 'end', undefined) as Date | string}
            />
            <div className="padding-left-20 padding-right-20 padding-top-15 padding-bottom-15">
                <Box rows={topRows} hasBorder={false} />
                {containsElectricVehicle && containsCombustionVehicle && tabEntity && (
                    <>
                        <VehiclesInfoTabs
                            dateRange={dateRange}
                            shouldUseV3Ratings={shouldUseV3Ratings}
                            shouldUseV2Ratings={false}
                            shouldShowHarshKPIs={false}
                            shouldShowExcessiveKPIs={false}
                            tabKey={tabKey}
                            setTabKey={setTabKey}
                            data={tabEntity}
                        />
                    </>
                )}
                {containsElectricVehicle && !containsCombustionVehicle && (
                    <>
                        <div className="margin-top-10 display-flex text-size-18 flex-row align-items-center">
                            <span className="margin-right-10">
                                <span className="rioglyph rioglyph-tachograph"></span>
                            </span>
                            <span className="text-size-18">
                                <FormattedMessage id="operatingData.label" />
                            </span>
                        </div>
                        <Box rows={vehicleElectricRows} hasBorder={false} />
                    </>
                )}
                {!containsElectricVehicle && containsCombustionVehicle && (
                    <>
                        <div className="margin-top-10 display-flex text-size-18 flex-row align-items-center">
                            <span className="margin-right-10">
                                <span className="rioglyph rioglyph-tachograph"></span>
                            </span>
                            <span className="text-size-18">
                                <FormattedMessage id="operatingData.label" />
                            </span>
                        </div>
                        <Box rows={vehicleCombustionRows} hasBorder={false} />
                    </>
                )}
                <Tabs
                    tabKey={tabKey}
                    dateRange={dateRange}
                    entity={data.vehicles?.length === 1 ? data : tabEntity}
                    allowedVehicles={data.vehicles?.filter(v => allowedVehicles.includes(v.id)).map(v => v.id) ?? []}
                    shouldShowOvertakeInCruise={shouldShowOvertakeInCruise}
                    containsElectricVehicle={containsElectricVehicle}
                />
            </div>
        </Sidebar>
    );
};

export const mapStateToProps = (state: State) => {
    return {
        data: driverSidebarData(state),
        dateRange: getActiveDateRange(state),
        allowedVehicles: getPerformVehicles(state),
        shouldShowOvertakeInCruise: getFeatureToggles(state).showOvertakeInCruise as boolean,
        shouldUseV3Ratings: getFeatureToggles(state).newOvertakeModel as boolean,
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        onVehicleClick: (data: Data) => dispatch(showSidebar({ data, type: DRIVER_ROUTE })),
        openParent: (id?: string) => {
            if (id) {
                dispatch(openRow({ id }));
            }
        },
        onClose: () => dispatch(hideSidebar()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(DriverSidebar);
